import { useMemo } from 'react';
import { ReviewDocumentResponse } from 'src/services/useReviewDocumentApi';
import { code } from '../GetColor';

const defaultValidationMessage: validationType = {
  message: 'fetching...',
  type: 'loading',
};

interface validationType {
  message: string;
  type: code;
}

export const CurrentCompanyOcrGenerateDocPermission = [
  'OcrGeneratedCurrentCompanyExactMatch',
  'OcrGeneratedCurrentCompanyMultipleExactMatch',
];
export const MultiplePossibleMatchDocPermission = [
  'OcrGeneratedCurrentCompanyPossibleMatch',
  'OcrGeneratedOtherCompanyPossibleMatch',
  'NonOcrGeneratedCurrentCompanyPossibleMatch',
  'NonOcrGeneratedOtherCompanyPossibleMatch',
  'NonOcrGeneratedCurrentCompanyExactMatch',
  'NonOcrGeneratedCurrentCompanyMultipleExactMatch',
];
export const OtherCompanyExactMatchDocPermission = [
  'OcrGeneratedOtherCompanyExactMatch',
  'NonOcrGeneratedOtherCompanyExactMatch',
  'NonOcrGeneratedOtherCompanyMultipleExactMatch',
  'OcrGeneratedOtherCompanyMultipleExactMatch',
];

const useDocumentValidationMessage = (
  docNumber: string,
  reviewDocumentValidations: ReviewDocumentResponse,
  selectedSupplier: {
    supplierName: string;
    supplierId: number | null;
  },
  isDocumentLoading: boolean,
  initialLoading: boolean,
  reviewDocumentError: string | null
) => {
  const documentValidationMessage = useMemo(() => {
    let documentValidationMessage = defaultValidationMessage;
    const docStatus = reviewDocumentValidations.matchStatus;
    const docMatchSupplier = reviewDocumentValidations.matchedSuppliers?.find(
      (s) => s.supplierId === selectedSupplier.supplierId
    );

    if (isDocumentLoading || initialLoading) {
      return (documentValidationMessage = defaultValidationMessage);
    }
    if (reviewDocumentError) {
      return (documentValidationMessage = { message: '', type: 'loading' });
    }
    if (!docNumber || docStatus === 'DocNumberNotFound') {
      documentValidationMessage = { message: 'Doc # is required.', type: 'error' };
    } else if (docStatus === 'NoMatch') {
      documentValidationMessage = { message: '', type: 'success' };
    } else if (CurrentCompanyOcrGenerateDocPermission.includes(docStatus)) {
      documentValidationMessage = {
        message: 'Doc # is a match to an existing, OCR generated doc.',
        type: 'warning',
      };
      if (
        (selectedSupplier.supplierId && docMatchSupplier && docMatchSupplier.hasOcrDocument) ||
        !selectedSupplier.supplierId
      ) {
        documentValidationMessage.type = 'error';
      }
    } else if (OtherCompanyExactMatchDocPermission.includes(docStatus)) {
      documentValidationMessage = {
        message:
          'Doc # is a match to an existing doc in another company. Consider moving record if assigned to the wrong company.',
        type: 'warning',
      };
    } else if (MultiplePossibleMatchDocPermission.includes(docStatus)) {
      documentValidationMessage = {
        message: 'Doc # is a possible match to multiple existing docs.',
        type: 'warning',
      };
    }

    return documentValidationMessage;
  }, [
    docNumber,
    initialLoading,
    isDocumentLoading,
    reviewDocumentError,
    reviewDocumentValidations.matchStatus,
    reviewDocumentValidations.matchedSuppliers,
    selectedSupplier.supplierId,
  ]);

  return documentValidationMessage;
};

export default useDocumentValidationMessage;
